import { gql, type TypedDocumentNode } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LazyMarkdown } from '@sorare/core/src/atoms/markdown/LazyMarkdown';
import { BodyS, HeadlineL } from '@sorare/core/src/atoms/typography';
import { Dialog } from '@sorare/core/src/components/dialog';
import {
  AnyRewardConfig,
  Variant,
} from '@sorare/core/src/components/rewards/AnyRewardConfig';
import { glossary } from '@sorare/core/src/lib/glossary';

import type { InboxTaskDialog_taskInterface } from './__generated__/index.graphql.ts';

type Props = {
  task: Nullable<InboxTaskDialog_taskInterface>;
  open: boolean;
  loading?: boolean;
  onClaim?: () => void;
  onClose: () => void;
};

export const InboxTaskDialog = ({
  task,
  open,
  loading,
  onClaim,
  onClose,
}: Props) => {
  const rewardConfig = task?.rewardConfigs.at(0);
  const claimable =
    !!onClaim && task?.aasmState === ManagerProgressionTaskState.COMPLETED;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      hideHeader
      blurBackdrop
      fullScreen={false}
      maxWidth="xs"
      fullWidth
    >
      {!!task && (
        <Vertical gap={5} className="p-2">
          <Vertical gap={2}>
            <HeadlineL>{task.title}</HeadlineL>
            <BodyS as="div" color="var(--c-nd-600)">
              <LazyMarkdown data={task.description} inModal />
            </BodyS>
          </Vertical>
          <SBHorizontal center>
            {!!rewardConfig && (
              <AnyRewardConfig
                anyRewardConfig={rewardConfig}
                variant={Variant.CHIP}
              />
            )}
            {!!claimable && (
              <Button
                size="large"
                color="primary"
                onClick={onClaim}
                disabled={loading}
              >
                <FormattedMessage {...glossary.claim} />
              </Button>
            )}
          </SBHorizontal>
        </Vertical>
      )}
    </Dialog>
  );
};

InboxTaskDialog.fragments = {
  taskInterface: gql`
    fragment InboxTaskDialog_taskInterface on TaskInterface {
      id
      title
      description
      aasmState
      rewardConfigs {
        id
        ...AnyRewardConfig_anyRewardConfigInterface
      }
    }
    ${AnyRewardConfig.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<InboxTaskDialog_taskInterface>,
};
